import { useCallback, useEffect, useRef } from 'react';

import { expValEquals, UNSAFE_expValEqualsNoExposure } from '@confluence/feature-experiments';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import type {
	ContentPermissionCapabilitySurface,
	ContentPermissionResult,
} from '../../contentPermissionStatus.ts';
import { getContentPermissionGateOutcome } from './realtimeAccessRequestsMetrics.ts';

const QUERY_LATENCY_SAMPLE_RATE = 0.01;
const REALTIME_ACCESS_REQUEST_FLAG = 'realtime-access-request-flag';

type InitialQueryTrigger = 'initial' | 'tab_resume';
export type RealtimeAccessRequestsQueryTrigger =
	| InitialQueryTrigger
	| 'request_created'
	| 'decision_made';
type RealtimeAccessRequestsQueryResult = 'completed' | 'failed';

type FireOperationalEvent = (args: {
	eventType: 'sendOperationalEvent';
	action: string;
	actionSubject: string;
	actionSubjectId: string;
	attributes: Record<string, unknown>;
	source: string;
}) => void;

type UseRealtimeAccessRequestsMetricsArgs = {
	contentId: string;
	contentPermissionCapabilitySurface: ContentPermissionCapabilitySurface | undefined;
	contentPermissionResult: ContentPermissionResult | undefined;
	isTabActive: boolean;
	isBaselineQuerySkipped: boolean;
	isQuerySkipped: boolean;
	fireAnalyticEvent: FireOperationalEvent;
};

type UseRealtimeAccessRequestsMetricsResult = {
	recordInitialQueryResult: (result: RealtimeAccessRequestsQueryResult) => InitialQueryTrigger;
	startQuery: (trigger: RealtimeAccessRequestsQueryTrigger) => void;
	recordQueryResult: (
		trigger: RealtimeAccessRequestsQueryTrigger,
		result: RealtimeAccessRequestsQueryResult,
	) => void;
};

export const useRealtimeAccessRequestsMetrics = ({
	contentId,
	contentPermissionCapabilitySurface,
	contentPermissionResult,
	isTabActive,
	isBaselineQuerySkipped,
	isQuerySkipped,
	fireAnalyticEvent,
}: UseRealtimeAccessRequestsMetricsArgs): UseRealtimeAccessRequestsMetricsResult => {
	const contentPermissionStatus = contentPermissionResult?.status;
	const capabilityUnavailableReason =
		contentPermissionResult?.unavailableReason ??
		(contentPermissionResult ? 'none' : 'capability_not_provided');
	const capabilitySource = contentPermissionCapabilitySurface
		? 'contentTypesHeader'
		: 'not_provided';
	const capabilitySurface = contentPermissionCapabilitySurface ?? 'not_provided';
	const queryStartedAtRef = useRef(Date.now());
	const initialQueryTriggerRef = useRef<InitialQueryTrigger>('initial');
	const wasTabActiveRef = useRef(isTabActive);
	const wasQuerySkippedRef = useRef(isQuerySkipped);
	const previousContentIdRef = useRef(contentId);
	const lastGatingMetricRef = useRef<string | undefined>(undefined);
	const queryDecisionRef = useRef({ contentId, recorded: false });

	useEffect(() => {
		if (queryDecisionRef.current.contentId !== contentId || isBaselineQuerySkipped) {
			queryDecisionRef.current = { contentId, recorded: false };
		}
		if (isBaselineQuerySkipped || queryDecisionRef.current.recorded) {
			return;
		}
		// A query already requested for this activation cannot be counted as saved later.
		if (!isQuerySkipped) {
			queryDecisionRef.current.recorded = true;
			return;
		}
		if (
			contentPermissionStatus === 'denied' &&
			UNSAFE_expValEqualsNoExposure('confluence_content_permission_gating', 'isEnabled', true)
		) {
			queryDecisionRef.current.recorded = true;
			getMonitoringClient().incrementCounter('realtimeAccessRequests.queryAvoided', [
				`capabilitySurface:${capabilitySurface}`,
				'experimentEnabled:true',
				'permissionStatus:denied',
			]);
		}
	}, [
		capabilitySurface,
		contentId,
		contentPermissionStatus,
		isBaselineQuerySkipped,
		isQuerySkipped,
	]);

	useEffect(() => {
		if (contentPermissionStatus === undefined) {
			return;
		}

		// Record exposure only when header permission data is available. The skip decision does not
		// record exposure.
		void expValEquals('confluence_content_permission_gating', 'isEnabled', true);

		const metricKey = `${contentId}:${contentPermissionStatus}:${capabilityUnavailableReason}:${capabilitySource}:${capabilitySurface}:${UNSAFE_expValEqualsNoExposure(
			'confluence_content_permission_gating',
			'isEnabled',
			true,
		)}`;
		if (lastGatingMetricRef.current === metricKey) {
			return;
		}
		lastGatingMetricRef.current = metricKey;

		const outcome = getContentPermissionGateOutcome(
			contentPermissionStatus,
			UNSAFE_expValEqualsNoExposure('confluence_content_permission_gating', 'isEnabled', true),
		);

		getMonitoringClient().incrementCounter('realtimeAccessRequests.contentPermissionGate', [
			`capabilityUnavailableReason:${capabilityUnavailableReason}`,
			`capabilitySource:${capabilitySource}`,
			`capabilitySurface:${capabilitySurface}`,
			`experimentEnabled:${UNSAFE_expValEqualsNoExposure(
				'confluence_content_permission_gating',
				'isEnabled',
				true,
			)}`,
			`outcome:${outcome}`,
			`permissionStatus:${contentPermissionStatus}`,
		]);
	}, [
		capabilitySource,
		capabilitySurface,
		capabilityUnavailableReason,
		contentId,
		contentPermissionStatus,
	]);

	useEffect(() => {
		if (previousContentIdRef.current !== contentId) {
			queryStartedAtRef.current = Date.now();
			initialQueryTriggerRef.current = 'initial';
		} else if (wasQuerySkippedRef.current && !isQuerySkipped) {
			queryStartedAtRef.current = Date.now();
			initialQueryTriggerRef.current = wasTabActiveRef.current ? 'initial' : 'tab_resume';
		}
		previousContentIdRef.current = contentId;
		wasQuerySkippedRef.current = isQuerySkipped;
		wasTabActiveRef.current = isTabActive;
	}, [contentId, isQuerySkipped, isTabActive]);

	const recordQueryResult = useCallback(
		(trigger: RealtimeAccessRequestsQueryTrigger, result: RealtimeAccessRequestsQueryResult) => {
			getMonitoringClient().incrementCounter('realtimeAccessRequests.queryExecution', [
				`capabilityUnavailableReason:${capabilityUnavailableReason}`,
				`capabilitySource:${capabilitySource}`,
				`capabilitySurface:${capabilitySurface}`,
				`experimentEnabled:${UNSAFE_expValEqualsNoExposure(
					'confluence_content_permission_gating',
					'isEnabled',
					true,
				)}`,
				`permissionStatus:${contentPermissionStatus ?? 'unavailable'}`,
				`result:${result}`,
				`trigger:${trigger}`,
			]);

			if (Math.random() >= QUERY_LATENCY_SAMPLE_RATE) {
				return;
			}

			fireAnalyticEvent({
				eventType: 'sendOperationalEvent',
				action: result,
				actionSubject: 'realtimeAccessRequestsQuery',
				actionSubjectId: trigger,
				source: REALTIME_ACCESS_REQUEST_FLAG,
				attributes: {
					capabilityUnavailableReason,
					capabilitySource,
					capabilitySurface,
					contentPermissionStatus: contentPermissionStatus ?? 'unavailable',
					durationMs: Date.now() - queryStartedAtRef.current,
					experimentEnabled: UNSAFE_expValEqualsNoExposure(
						'confluence_content_permission_gating',
						'isEnabled',
						true,
					),
					sampleRate: QUERY_LATENCY_SAMPLE_RATE,
					trigger,
				},
			});
		},
		[
			capabilitySource,
			capabilitySurface,
			capabilityUnavailableReason,
			contentPermissionStatus,
			fireAnalyticEvent,
		],
	);

	const recordInitialQueryResult = useCallback(
		(result: RealtimeAccessRequestsQueryResult): InitialQueryTrigger => {
			recordQueryResult(initialQueryTriggerRef.current, result);
			return initialQueryTriggerRef.current;
		},
		[recordQueryResult],
	);

	const startQuery = useCallback((_trigger: RealtimeAccessRequestsQueryTrigger) => {
		queryStartedAtRef.current = Date.now();
	}, []);

	return {
		recordInitialQueryResult,
		startQuery,
		recordQueryResult,
	};
};
